import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { submitLead } from "@/lib/api";
import { TEAM_SIZE_OPTIONS, OFFICE_TYPE_OPTIONS } from "@/lib/site";
import { TESTIDS } from "@/constants/testIds/site";

const initial = {
  full_name: "",
  company_name: "",
  email: "",
  phone: "",
  team_size: "",
  move_in_date: "",
  office_type: "",
  notes: "",
};

export const InquiryForm = ({
  defaultOfficeType = "",
  sourcePage = "",
  heading = "Request availability",
  subheading = "Tell us a little about your team and we'll send tailored options within one business hour.",
}) => {
  const [data, setData] = useState({ ...initial, office_type: defaultOfficeType });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!data.full_name.trim() || !data.email.trim() || !data.phone.trim()) {
      setError("Please share your name, email and phone so we can get back to you.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({ ...data, source_page: sourcePage });
      setSubmitted(true);
      toast.success("Thanks — your inquiry is in. We'll be in touch shortly.");
      setData({ ...initial, office_type: defaultOfficeType });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please call us at the number to the right.";
      setError(String(msg));
      toast.error("We couldn't submit your inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid={TESTIDS.inquirySuccess}
        className="hairline bg-white p-10 md:p-14"
      >
        <div className="w-12 h-12 rounded-full bg-[var(--primary)] text-white flex items-center justify-center">
          <Check size={20} strokeWidth={2} />
        </div>
        <h3 className="font-serif text-[34px] mt-6 leading-tight">
          Inquiry received.
        </h3>
        <p className="mt-3 text-[15px] text-[var(--text-muted)] max-w-md">
          A member of the leasing team will reach out within one business hour
          with availability and a recommended tour time.
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className="mt-8 link-underline"
        >
          Submit another inquiry <ArrowRight size={14} />
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid={TESTIDS.inquiryForm}
      className="hairline bg-white p-8 md:p-12"
      noValidate
    >
      <div className="eyebrow">Leasing inquiry</div>
      <h3 className="font-serif text-[34px] md:text-[42px] mt-2 leading-[1.05] tracking-tight">
        {heading}
      </h3>
      <p className="mt-3 text-[15px] text-[var(--text-muted)] max-w-lg">
        {subheading}
      </p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <div className="md:col-span-1">
          <label className="input-label" htmlFor="full_name">Full name *</label>
          <input
            id="full_name"
            data-testid={TESTIDS.inputFullName}
            className="input-clean"
            value={data.full_name}
            onChange={update("full_name")}
            placeholder="Jane Caldwell"
            required
          />
        </div>
        <div>
          <label className="input-label" htmlFor="company_name">Company</label>
          <input
            id="company_name"
            data-testid={TESTIDS.inputCompany}
            className="input-clean"
            value={data.company_name}
            onChange={update("company_name")}
            placeholder="Caldwell Advisory Group"
          />
        </div>
        <div>
          <label className="input-label" htmlFor="email">Email *</label>
          <input
            id="email"
            type="email"
            data-testid={TESTIDS.inputEmail}
            className="input-clean"
            value={data.email}
            onChange={update("email")}
            placeholder="jane@firm.com"
            required
          />
        </div>
        <div>
          <label className="input-label" htmlFor="phone">Phone *</label>
          <input
            id="phone"
            type="tel"
            data-testid={TESTIDS.inputPhone}
            className="input-clean"
            value={data.phone}
            onChange={update("phone")}
            placeholder="954-384-6949"
            required
          />
        </div>
        <div>
          <label className="input-label" htmlFor="team_size">Team size</label>
          <select
            id="team_size"
            data-testid={TESTIDS.inputTeamSize}
            className="input-clean appearance-none bg-transparent cursor-pointer"
            value={data.team_size}
            onChange={update("team_size")}
          >
            <option value="">Select a range</option>
            {TEAM_SIZE_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="input-label" htmlFor="move_in_date">Desired move-in</label>
          <input
            id="move_in_date"
            type="date"
            data-testid={TESTIDS.inputMoveInDate}
            className="input-clean"
            value={data.move_in_date}
            onChange={update("move_in_date")}
          />
        </div>
        <div className="md:col-span-2">
          <label className="input-label" htmlFor="office_type">Office type</label>
          <select
            id="office_type"
            data-testid={TESTIDS.inputOfficeType}
            className="input-clean appearance-none bg-transparent cursor-pointer"
            value={data.office_type}
            onChange={update("office_type")}
          >
            <option value="">Select a workspace</option>
            {OFFICE_TYPE_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="input-label" htmlFor="notes">Anything else?</label>
          <textarea
            id="notes"
            data-testid={TESTIDS.inputNotes}
            className="input-clean resize-none min-h-[110px] pt-3"
            rows={4}
            value={data.notes}
            onChange={update("notes")}
            placeholder="Parking needs, signage, must-haves, timing flexibility…"
          />
        </div>
      </div>

      {error && (
        <p
          data-testid={TESTIDS.inquiryError}
          className="mt-6 text-[13px] text-[#A03A30]"
        >
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        data-testid={TESTIDS.inquirySubmit}
        className="btn-primary mt-10 w-full md:w-auto disabled:opacity-60"
      >
        {submitting ? "Sending…" : "Send leasing inquiry"}
        <ArrowRight size={14} strokeWidth={1.7} />
      </button>

      <p className="mt-5 text-[12px] text-[var(--text-muted)]">
        By submitting, you agree to be contacted by our leasing team. We never
        share your details with anyone outside Weston Commercial Center.
      </p>
    </form>
  );
};

export default InquiryForm;
